import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { generateArtworkFromActivity } from '../lib/openai';

const { FiActivity, FiPalette, FiFeather, FiImage, FiArrowLeft } = FiIcons;

const StravaAnalysisPage = ({ activity }) => {
  const navigate = useNavigate();
  const [analysisData, setAnalysisData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Generate analysis based on activity data
    const analyzeActivity = async () => {
      if (!activity) return;
      
      setLoading(true);
      setError(null);
      
      try {
        // Use OpenAI to generate artwork analysis
        const result = await generateArtworkFromActivity(activity);
        setAnalysisData(result);
      } catch (err) {
        console.error('Error analyzing activity:', err);
        setError('Failed to generate analysis. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    if (activity) {
      analyzeActivity();
    }
  }, [activity]);

  // Button handlers
  const handleGenerateNewStyle = async () => {
    if (!activity) return;
    
    setRegenerating(true);
    setError(null);
    
    try {
      // Generate a new artwork style while keeping the same activity
      const result = await generateArtworkFromActivity(activity);
      
      // Just update the painting style while keeping the poem
      setAnalysisData(prev => ({
        ...prev,
        artwork: result.artwork
      }));
    } catch (err) {
      console.error('Error generating new style:', err);
      setError('Failed to generate a new style. Please try again.');
    } finally {
      setRegenerating(false);
    }
  };

  const handleTryDifferentPoet = async () => {
    if (!activity) return;
    
    setRegenerating(true);
    setError(null);
    
    try {
      // Generate a new poetic interpretation while keeping the same activity
      const result = await generateArtworkFromActivity(activity);
      
      // Just update the poetry while keeping the artwork style
      setAnalysisData(prev => ({
        ...prev,
        title: result.title,
        poem: result.poem
      }));
    } catch (err) {
      console.error('Error generating new poetry:', err);
      setError('Failed to generate new poetry. Please try again.');
    } finally {
      setRegenerating(false);
    }
  };

  const handleCreateVisualArt = () => {
    // Navigate to the activity detail page with the artwork tab active
    if (activity && activity.id) {
      navigate(`/activity/${activity.id}`);
    } else {
      // If no specific activity, navigate to dashboard
      navigate('/dashboard');
    }
  };

  const handleBackToDashboard = () => {
    navigate('/dashboard');
  };

  if (loading || regenerating) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <motion.div 
          className="text-center text-white"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
            className="mb-4"
          >
            <SafeIcon icon={FiActivity} className="w-12 h-12 text-orange-400 mx-auto" />
          </motion.div>
          <p className="text-xl font-medium mb-2">Analyzing your activity</p>
          <p className="text-gray-400">{regenerating ? "Creating new artistic interpretation..." : "Generating artistic analysis..."}</p>
        </motion.div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-6 flex items-center justify-center">
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 max-w-md w-full text-center">
          <div className="text-red-400 mb-4">
            <SafeIcon icon={FiActivity} className="w-12 h-12 mx-auto" />
          </div>
          <h2 className="text-xl font-bold text-white mb-4">Analysis Error</h2>
          <p className="text-gray-300 mb-6">{error}</p>
          <button 
            onClick={handleBackToDashboard}
            className="px-6 py-2 bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600 text-white rounded-lg transition-colors"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (!analysisData || !activity) return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-6 flex items-center justify-center">
      <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 max-w-md w-full text-center">
        <h2 className="text-xl font-bold text-white mb-4">No Activity Selected</h2>
        <p className="text-gray-300 mb-6">Please select an activity from your dashboard to analyze.</p>
        <button 
          onClick={handleBackToDashboard}
          className="px-6 py-2 bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600 text-white rounded-lg transition-colors"
        >
          Go to Dashboard
        </button>
      </div>
    </div>
  );

  const formatPace = (metersPerSecond) => {
    const minutesPerKm = 1000 / (metersPerSecond * 60);
    const minutes = Math.floor(minutesPerKm);
    const seconds = Math.floor((minutesPerKm - minutes) * 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}/km`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header with back button */}
        <div className="flex items-center mb-8">
          <button 
            onClick={handleBackToDashboard}
            className="flex items-center gap-2 text-white hover:text-orange-400 transition-colors"
          >
            <SafeIcon icon={FiArrowLeft} className="w-5 h-5" />
            Back to Dashboard
          </button>
        </div>
      
        {/* Main content header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <h1 className="text-3xl font-bold text-white mb-4">
            Artistic Interpretation: {activity.name}
          </h1>
          <p className="text-gray-300 max-w-2xl mx-auto">
            We've analyzed your {activity.type.toLowerCase()} data including pace, elevation, and rhythm patterns 
            to create a unique artistic interpretation.
          </p>
        </motion.div>

        {/* Analysis Sections */}
        <div className="space-y-8">
          {/* Artwork Theme */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6"
          >
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
              🖼️ Artwork Theme
            </h2>
            <div className="space-y-4 text-gray-300">
              <p>
                Your {activity.type.toLowerCase()} of {(activity.distance / 1000).toFixed(2)}km at 
                {formatPace(activity.average_speed)} pace with {activity.total_elevation_gain}m elevation gain 
                has been transformed into a unique artistic expression.
              </p>
              <p>
                Theme: <span className="text-orange-400 font-medium">{analysisData.artwork.theme}</span>
              </p>
            </div>
          </motion.section>

          {/* Painting Concept */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6"
          >
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
              🎨 Painting Concept
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-orange-400 font-semibold mb-2">Style: {analysisData.artwork.style}</h3>
                <p className="text-gray-300 mb-4">
                  Inspired by {analysisData.artwork.poeticInfluence}'s poetic sensibilities, 
                  this artwork captures the essence of your activity through visual expression.
                </p>
              </div>
              <div className="bg-black/20 rounded-xl p-4">
                <h4 className="text-sm uppercase tracking-wider text-gray-400 mb-2">Color Palette</h4>
                <p className="text-white">{analysisData.artwork.palette}</p>
              </div>
            </div>
          </motion.section>

          {/* Poetry Section */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6"
          >
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
              ✒️ Title & Description Poem
            </h2>
            
            <div className="space-y-6">
              <div className="bg-gradient-to-r from-orange-500/10 to-pink-500/10 border border-orange-500/20 rounded-xl p-6">
                <h3 className="text-orange-400 font-semibold mb-2">Title</h3>
                <p className="text-white text-xl font-medium italic">
                  {analysisData.title}
                </p>
              </div>
              
              <div className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/20 rounded-xl p-6">
                <h3 className="text-blue-400 font-semibold mb-2">Description</h3>
                <p className="text-white text-lg italic leading-relaxed">
                  {analysisData.poem}
                </p>
              </div>
            </div>
          </motion.section>

          {/* Call to Action */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="bg-gradient-to-r from-orange-500/20 to-pink-500/20 border border-orange-500/30 rounded-2xl p-6"
          >
            <p className="text-white text-center mb-6">
              Would you like to try a different artistic style, poetic interpretation, or create a visual artwork based on this analysis?
            </p>
            
            <div className="flex flex-wrap justify-center gap-4">
              <motion.button
                onClick={handleGenerateNewStyle}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600 text-white font-medium rounded-xl transition-all duration-300"
                disabled={regenerating}
              >
                <SafeIcon icon={FiPalette} className="w-5 h-5" />
                Generate New Style
              </motion.button>
              
              <motion.button
                onClick={handleTryDifferentPoet}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="flex items-center gap-2 px-6 py-3 bg-white/10 hover:bg-white/20 text-white font-medium rounded-xl transition-all duration-300"
                disabled={regenerating}
              >
                <SafeIcon icon={FiFeather} className="w-5 h-5" />
                Try Different Poet
              </motion.button>
              
              <motion.button
                onClick={handleCreateVisualArt}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="flex items-center gap-2 px-6 py-3 bg-blue-500/20 hover:bg-blue-500/30 text-blue-300 font-medium rounded-xl transition-all duration-300"
              >
                <SafeIcon icon={FiImage} className="w-5 h-5" />
                Create Visual Art
              </motion.button>
            </div>
          </motion.section>
        </div>
      </div>
    </div>
  );
};

export default StravaAnalysisPage;